import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { IcReceipt, IcBarChart, IcSearch, IcShoppingCart, IcShoppingBag, IcBanknote, IcTruck, IcSparkles } from '../../theme/icons';
import statisticsService from '../../services/statisticsService';
import { fmt, StatsChip } from './statsUi';
import { StatsFilterBar, DEFAULT_FILTER, resolveRange } from './StatsFilter';
import { CardSkeleton, Sk } from '../../components/Skeleton';

// Page « Historique d'action » — flux unifié de toutes les actions (vente /
// achat / charge / paiement fournisseur / soin) : cases KPI + tableau dans
// une carte blanche (owner 2026-08-16). Owner 2026-09-16 : le filtre de
// période est le MÊME que « Statistique générale » (StatsFilterBar : pilules
// 7 j / 30 j / 90 j / Ce mois / Cette année + plage personnalisée ‹ 📅 › ),
// à la place de la navigation par jour + « Voir tout (30 j) ».

// Identité de chaque type d'action : pill du tableau (Etiquettes-2 §10 :
// `bdg` + variante de sens + icône Lucide imposée) + case KPI.
const ACTION_META = {
  vente: { label: 'Vente', icon: IcShoppingCart, pill: 'bdg-pos', tint: 'bg-emerald-50 text-emerald-600' },
  achat: { label: 'Achat', icon: IcShoppingBag, pill: 'bdg-achat', tint: 'bg-sky-50 text-sky-600' },
  charge: { label: 'Charge', icon: IcBanknote, pill: 'bdg-charge', tint: 'bg-teal-50 text-teal-600' },
  'paiement fournisseur': { label: 'Paiement fournisseur', icon: IcTruck, pill: 'bdg-payfour', tint: 'bg-orange-50 text-orange-600' },
  soin: { label: 'Soin', icon: IcSparkles, pill: 'bdg-type-soin', tint: 'bg-rose-50 text-rose-600' },
};
const TYPE_TABS = [
  { value: '', label: 'Toutes' },
  ...Object.entries(ACTION_META).map(([value, m]) => ({ value, label: m.label })),
];

const ActionHistory = () => {
  // filter = { period } (période rapide) OU { start, end } (plage libre) —
  // même filtre que « Statistique générale » / « Statistique de ventes ».
  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [typeFilter, setTypeFilter] = useState('');
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Plage d'un seul jour (calendrier : début = fin) → colonne « Heure ».
  const range = resolveRange(filter);
  const singleDay = range.start.getTime() === range.end.getTime();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    statisticsService.getActionHistory(filter.period ? { period: filter.period } : { start: filter.start, end: filter.end })
      .then((d) => { if (alive) { setFeed(d); setError(null); } })
      .catch(() => { if (alive) { setFeed(null); setError("Impossible de charger l'historique."); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filter]);

  const actions = useMemo(() => feed?.actions || [], [feed]);
  const rows = typeFilter ? actions.filter((a) => a.type === typeFilter) : actions;

  // Cases KPI : total + nombre par type sur ce qui est affiché (jour ou période).
  const totals = useMemo(() => {
    const t = {};
    for (const a of actions) {
      const k = a.type;
      if (!t[k]) t[k] = { count: 0, amount: 0 };
      t[k].count += 1;
      t[k].amount += parseFloat(a.amount) || 0;
    }
    return t;
  }, [actions]);

  const fmtRowDate = (d) => { try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (_) { return d; } };
  // Plage d'un seul jour : toutes les lignes partagent la même date, n'afficher que l'heure.
  const fmtRowTime = (d) => { try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch (_) { return d; } };

  return (
    <div className="w-full px-4 pt-2 pb-4">
      <StatsChip
        icon={IcReceipt} title="Historique d'action"
        right={
          <Link
            to="/statistiques/generale"
            className="px-4 py-2.5 rounded-xl border bg-white border-gray-200 text-gray-500 hover:text-gray-700 hover:border-gray-300 flex items-center justify-center font-medium transition-colors"
          >
            <IcBarChart className="mr-2 text-sm" />
            Statistique générale
          </Link>
        }
      />

      <StatsFilterBar filter={filter} onChange={setFilter} />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 mb-4 rounded-xl">{error}</div>}

      {/* Cases KPI — une par type d'action. Owner 2026-09-16 : mêmes cases que
          la barre KPI de /seller-history et /service-history — kit .kpi, icône
          NEUTRE 11px en haut à droite (.kpi-icon) à la place de la pastille 40px
          teintée, libellé en capitales, valeur tabulaire, sous-ligne .kpi-sub. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4 mb-6">
        <div className="kpi min-w-0 rounded-2xl p-3">
          <IcReceipt size={11} className="kpi-icon w-[11px] h-[11px]" />
          <div className="kpi-label uppercase pr-6 text-xs mb-1 truncate">Actions</div>
          <div className="kpi-value text-base sm:text-lg truncate">{actions.length}</div>
        </div>
        {Object.entries(ACTION_META).map(([key, m]) => {
          const t = totals[key] || { count: 0, amount: 0 };
          return (
            <div key={key} className="kpi min-w-0 rounded-2xl p-3">
              <m.icon size={11} className="kpi-icon w-[11px] h-[11px]" />
              <div className="kpi-label uppercase pr-6 text-xs mb-1 truncate">{m.label}{t.count > 1 ? 's' : ''}</div>
              <div className="kpi-value text-base sm:text-lg truncate">{fmt(t.amount)}</div>
              <div className="kpi-sub truncate">{t.count} action{t.count > 1 ? 's' : ''}</div>
            </div>
          );
        })}
      </div>

      {/* Tableau des actions — carte blanche, chips de type dans l'en-tête. */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            {TYPE_TABS.map((tb) => {
              const active = typeFilter === tb.value;
              return (
                <button
                  key={tb.value || 'all'}
                  onClick={() => setTypeFilter(tb.value)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition border cursor-pointer ${
                    active ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {tb.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            {loading && <Sk w={40} h={10} className="sk-inline" />}
            <span className="bdg bdg-pill">
              {rows.length} action{rows.length !== 1 ? 's' : ''}
              {typeFilter && ` · ${actions.length} au total`}
            </span>
          </div>
        </div>

        {loading && !feed ? (
          <div className="text-center py-12">
            <CardSkeleton />
          </div>
        ) : rows.length === 0 ? (
          /* État vide (Brand Kit §05) : cadre pointillé, icône 22px, phrase 13px 600 */
          <div className="empty-ki m-4 py-14">
            <IcSearch className="empty-ki-icon w-12 h-12 mb-4" />
            <p className="empty-ki-title text-lg mt-0">
              Aucune action sur la période.
            </p>
            <p className="empty-ki-sub mb-0">Essayez une autre période.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {/* Tableau dense (Brand Kit §05) : en-tête th-ki #e9e9ea 8/12px,
                lignes 13px 8/12px, séparateurs gray-100, survol #F0FDF4 */}
            <table className="min-w-full divide-y divide-gray-100">
              <thead>
                <tr>
                  {[singleDay ? 'Heure' : 'Date', 'Type', 'Libellé', 'Vendeur', 'Montant'].map((h) => (
                    <th key={h} className={`th-ki px-5 py-3 text-xs tracking-wider uppercase ${
                      h === 'Montant' ? 'text-right' : 'text-left'
                    }`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {rows.map((a, i) => {
                  const m = ACTION_META[a.type] || { label: a.type, pill: 'bdg-neutral' };
                  return (
                    <tr key={i} className="hover:bg-gray-100 transition">
                      <td className="td-ki px-5 py-3.5 text-sm whitespace-nowrap text-gray-600 tabular-nums">
                        {singleDay ? fmtRowTime(a.date) : fmtRowDate(a.date)}
                      </td>
                      <td className="td-ki px-5 py-3.5 whitespace-nowrap">
                        {/* livre §04 journal : libellé seul (l'icône reste sur la case KPI) */}
                        <span className={`bdg ${m.pill}`}>{m.label}</span>
                      </td>
                      <td className="td-ki px-5 py-3.5 text-sm text-gray-700">
                        <span className="block truncate max-w-[420px]">{a.label}</span>
                      </td>
                      {/* Qui a fait l'action (owner 2026-10-14) : vendeur de la vente, ou
                          la personne reconnue à son code pour les autres familles. */}
                      <td className={`td-ki px-5 py-3.5 text-sm whitespace-nowrap ${a.seller ? 'text-gray-700' : 'text-gray-400'}`}>
                        {a.seller || '—'}
                      </td>
                      <td className="td-ki px-5 py-3.5 text-sm whitespace-nowrap font-semibold text-gray-900 text-right tabular-nums">
                        {fmt(a.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionHistory;
